import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import T from 'i18n-react';
import { CommentBox, EmptyState, Loader } from '@adsk/bim360-matrix-react-components';
import { connect } from '../../../../redux/connect';
import {
  fetchChannelItemsAction,
  createCommentAction,
  trackEvent,
} from '../../../../redux/actions';
import {
  getCurrentUserInMcFormat,
  getGroupedProjectActiveEntitiesForMentionComponent,
  getGroupedProjectActiveEntitiesForMentionComponentWithRolesInUse,
  getCurrentPreviewIssueChannelItemsWrapper,
  getCurrentPreviewIssueSortedChannelItems,
} from '../../../../redux/selectors';
import featureFlags, { FEATURE_FLAGS } from '../../../../utils/featureFlags';
import { issueAttributesType } from '../../../../constants/proptype-validation';
import { addComment } from '../../../../utils/analytics-helpers';
import { canAddComment, canAddAttachment } from '../../../../utils/issues-helpers';
import AttachmentUpload from './components/AttachmentUpload';
import ChannelItems from './components/ChannelItems';

class IssuePreviewActivityTab extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      commentBoxFocused: false,
      isSendingComment: false,
      isCommentBoxDirty: false,
    };

    this.fetchChannelItems = this.fetchChannelItems.bind(this);
  }

  componentWillMount() {
    this.fetchChannelItems();
  }

  fetchChannelItems() {
    const { issue, fetchChannelItemsAction } = this.props;
    const issueId = issue.id;

    fetchChannelItemsAction(issueId);
  }

  renderChannelItems() {
    const { issue, channelItems } = this.props;
    const issueId = issue.id;

    return (
      <ChannelItems
        className="isui-channel__channel-items"
        key={`${issueId}-channel-${issue.updatedAt}`}
        issueId={issueId}
        channelItems={channelItems}
      />
    );
  }

  createComment(commentText) {
    const { issue, createCommentAction, trackEvent } = this.props;
    const issueId = issue.id;
    this.setState({ isSendingComment: true });

    // TODO - this is "hacky" we should figure out a different way to figure out if the comment contains a mention
    const isMention = commentText.includes('@{"type":');

    return new Promise((resolve, reject) =>
      createCommentAction(
        issueId,
        commentText,
        () => {
          this.setState({ isSendingComment: false });
          // MixPanel Event - when user add a comment
          trackEvent(addComment(isMention));
          resolve();
        },
        () => {
          this.setState({ isSendingComment: false });
          reject();
        }
      )
    );
  }

  renderAttachmentUploadButton() {
    const { issue, container } = this.props;
    const { commentBoxFocused, isCommentBoxDirty, isSendingComment } = this.state;
    const isFieldDirty = commentBoxFocused || isCommentBoxDirty;
    if (isFieldDirty || isSendingComment || !canAddAttachment(issue)) {
      return null;
    }
    return <AttachmentUpload container={container} />;
  }

  commentBoxOnChange(text, commentEditorState) {
    this.props.saveActivityCommentEditorState(commentEditorState);
    this.setState({ isCommentBoxDirty: !!text.trim() });
  }

  renderCommentBox() {
    const {
      issue,
      currentUserInMcFormat,
      groupedProjectEntitiesForMention,
      disableKeyboard,
      activityCommentEditorState,
    } = this.props;
    if (!canAddComment(issue)) {
      return null;
    }

    return (
      <div
        className="IssuePreviewActivityTab__footer"
        data-testid="IssuePreviewActivityTab__footer"
      >
        <CommentBox
          className="IssuePreviewActivityTab__comment-box"
          inputClassName="IssuePreviewActivityTab__comment-box-input"
          data-testid="IssuePreviewActivityTab__comment-box-input"
          currentUser={currentUserInMcFormat}
          suggestions={groupedProjectEntitiesForMention}
          editorState={activityCommentEditorState}
          onFocus={() => {
            disableKeyboard(true);
            this.setState({ commentBoxFocused: true });
          }}
          onBlur={() => {
            this.setState({ commentBoxFocused: false });
            disableKeyboard(false);
          }}
          onChange={(text, commentEditorState) => this.commentBoxOnChange(text, commentEditorState)}
          onKeyUp={(event) => {
            // Since the Modal is attaching to the 'keyup' event of the body,
            // and we're going to call "disableKeyboard(false)" right now,
            // the modal event handler (that's going to be invoked after this handler) is going to think it needs to handle the keyboard.
            // That's why we need to stopPropagation and preventDefault right now to prevent the modal from handling this event.
            if (event.keyCode === 27 && !this.state.commentBoxFocused) {
              event.stopPropagation();
              event.preventDefault();
              disableKeyboard(false);
            }
          }}
          onSend={(commentText) => this.createComment(commentText)}
          getSuggestionId={(user) => user.oxygenId}
          getSuggestionName={(user) => user.name}
          getMentionId={(user) => user.oxygenId}
          getMentionName={(user) => user.name}
          getMentionType={(user) => user.oxygenType}
        />
        {this.renderAttachmentUploadButton()}
      </div>
    );
  }

  render() {
    const { isLoading, channelItems, currentUserInMcFormat } = this.props;

    if ((isLoading && channelItems.length === 0) || !currentUserInMcFormat) {
      return <Loader />;
    }

    if (channelItems.length === 0) {
      return (
        <div className="IssuePreviewActivityTab__empty-state">
          <EmptyState
            icon={EmptyState.Icons.CONE}
            text={T.translate('activity.error')}
            footer={
              <div
                className="IssuePreviewActivityTab__activity-error-try-again"
                onClick={this.fetchChannelItems}
              >
                {T.translate('activity.tryAgain')}
              </div>
            }
          />
        </div>
      );
    }

    return (
      <div className="IssuePreviewActivityTab" data-testid="IssuePreviewActivityTab">
        <div className="IssuePreviewActivityTabActivityList">
          {this.renderChannelItems()}
          {this.renderCommentBox()}
        </div>
      </div>
    );
  }
}

IssuePreviewActivityTab.displayName = 'IssuePreviewActivityTab';

IssuePreviewActivityTab.propTypes = {
  issue: issueAttributesType,
  isLoading: PropTypes.bool,
  fetchChannelItemsAction: PropTypes.func.isRequired,
  channelItems: PropTypes.array,
  currentUserInMcFormat: PropTypes.object,
  createCommentAction: PropTypes.func.isRequired,
  disableKeyboard: PropTypes.func.isRequired,
  trackEvent: PropTypes.func,
  groupedProjectEntitiesForMention: PropTypes.array.isRequired,
  container: PropTypes.object.isRequired,
  saveActivityCommentEditorState: PropTypes.func,
  activityCommentEditorState: PropTypes.object,
};

const mapStateToProps = (state) => {
  const channelItemsWrapper = getCurrentPreviewIssueChannelItemsWrapper(state);
  const channelItems = getCurrentPreviewIssueSortedChannelItems(state);

  return {
    isLoading: channelItemsWrapper.loading,
    channelItems,
    currentUserInMcFormat: getCurrentUserInMcFormat(state),
    groupedProjectEntitiesForMention: featureFlags.get(FEATURE_FLAGS.RFIS_CUSTOM_ROLES)
      ? getGroupedProjectActiveEntitiesForMentionComponentWithRolesInUse(state)
      : getGroupedProjectActiveEntitiesForMentionComponent(state),
  };
};

const mapDispatchToProps = {
  fetchChannelItemsAction,
  createCommentAction,
  trackEvent,
};

export default connect(mapStateToProps, mapDispatchToProps)(IssuePreviewActivityTab);
